<template>
  <div class="fixed bottom-4 right-4 z-50">
    <!-- Toggle Button -->
    <button
      v-if="!showDebug"
      @click="showDebug = true"
      class="bg-red-600 text-white p-3 rounded-full shadow-lg hover:bg-red-700 transition-colors"
      title="Debug Contenu"
    >
      🔧
    </button>

    <!-- Debug Panel -->
    <div
      v-if="showDebug"
      class="bg-white border border-gray-300 rounded-lg shadow-xl p-4 w-96 max-h-96 overflow-y-auto"
    >
      <!-- Header -->
      <div class="flex justify-between items-center mb-3 pb-2 border-b">
        <h3 class="font-semibold text-gray-900">Debug Contenu</h3>
        <div class="flex space-x-2">
          <button
            @click="loadContent"
            class="text-blue-600 hover:text-blue-800 text-sm"
            :disabled="loading"
          >
            {{ loading ? '🔄' : '↻' }} Recharger
          </button>
          <button
            @click="showDebug = false"
            class="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-4">
        <div class="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <p class="text-sm text-gray-500 mt-2">Chargement...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded p-3 mb-3">
        <p class="text-red-800 text-sm font-medium">Erreur:</p>
        <p class="text-red-600 text-xs">{{ error }}</p>
        <button
          @click="testConnection"
          class="mt-2 text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700"
        >
          Tester connexion
        </button>
      </div>

      <!-- Content List -->
      <div v-else>
        <!-- Stats -->
        <div class="bg-gray-50 rounded p-2 mb-3 text-xs">
          <div class="grid grid-cols-2 gap-2">
            <div>Total: {{ content.length }}</div>
            <div>Pages: {{ uniquePages.length }}</div>
            <div>Catégories: {{ uniqueCategories.length }}</div>
            <div>Types: {{ uniqueTypes.length }}</div>
          </div>
        </div>

        <!-- Filters -->
        <div class="mb-3 space-y-2">
          <select
            v-model="filterPage"
            class="w-full text-xs border border-gray-300 rounded px-2 py-1"
          >
            <option value="">Toutes les pages</option>
            <option v-for="page in uniquePages" :key="page" :value="page">
              {{ page }}
            </option>
          </select>
          <select
            v-model="filterCategory"
            class="w-full text-xs border border-gray-300 rounded px-2 py-1"
          >
            <option value="">Toutes les catégories</option>
            <option v-for="category in uniqueCategories" :key="category" :value="category">
              {{ category }}
            </option>
          </select>
        </div>

        <!-- Content Items -->
        <div class="space-y-2 max-h-48 overflow-y-auto">
          <div
            v-for="item in filteredContent"
            :key="item.id"
            class="bg-gray-50 rounded p-2 text-xs"
          >
            <div class="flex justify-between items-start mb-1">
              <code class="text-blue-600 font-mono">{{ item.key }}</code>
              <span class="text-gray-500 text-xs">{{ item.type }}</span>
            </div>
            <div class="text-gray-700 truncate" :title="item.value">
              {{ item.value || 'Vide' }}
            </div>
            <div class="text-gray-400 text-xs mt-1">
              {{ item.page }} / {{ item.category }}
            </div>
          </div>
        </div>

        <!-- No Content -->
        <div v-if="filteredContent.length === 0" class="text-center py-4 text-gray-500 text-sm">
          Aucun contenu trouvé
          <button
            @click="initializeContent"
            class="block mx-auto mt-2 text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
          >
            Initialiser le contenu
          </button>
        </div>
      </div>

      <!-- Actions -->
      <div class="mt-3 pt-2 border-t flex space-x-2">
        <button
          @click="openContentManager"
          class="flex-1 text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700"
        >
          Ouvrir CMS
        </button>
        <button
          @click="openAdmin"
          class="flex-1 text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
        >
          Admin
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { contentService, type ContentBlock } from '@/services/content'
import { baserowService } from '@/services/baserow'

const router = useRouter()

// State
const showDebug = ref(false)
const loading = ref(false)
const error = ref('')
const content = ref<ContentBlock[]>([])
const filterPage = ref('')
const filterCategory = ref('')

// Computed
const uniquePages = computed(() => {
  return [...new Set(content.value.map(c => c.page))].sort()
})

const uniqueCategories = computed(() => {
  return [...new Set(content.value.map(c => c.category))].sort()
})

const uniqueTypes = computed(() => {
  return [...new Set(content.value.map(c => c.type))].sort()
})

const filteredContent = computed(() => {
  let filtered = content.value

  if (filterPage.value) {
    filtered = filtered.filter(c => c.page === filterPage.value)
  }

  if (filterCategory.value) {
    filtered = filtered.filter(c => c.category === filterCategory.value)
  }

  return filtered.sort((a, b) => a.order - b.order)
})

// Methods
const loadContent = async () => {
  loading.value = true
  error.value = ''
  
  try {
    content.value = await contentService.getContent()
    console.log('Debug: Contenu chargé', content.value)
  } catch (err: any) {
    error.value = err.message || 'Erreur inconnue'
    console.error('Debug: Erreur chargement contenu', err)
  } finally {
    loading.value = false
  }
}

const testConnection = async () => {
  loading.value = true
  try {
    const isConnected = await baserowService.testConnection()
    if (isConnected) {
      error.value = ''
      await loadContent()
    } else {
      error.value = 'Connexion Baserow échouée'
    }
  } catch (err: any) {
    error.value = err.message || 'Test de connexion échoué'
  } finally {
    loading.value = false
  }
}

const initializeContent = async () => {
  loading.value = true
  try {
    await contentService.initializeDefaultContent()
    await loadContent()
  } catch (err: any) {
    error.value = err.message || 'Initialisation échouée'
  } finally {
    loading.value = false
  }
}

const openContentManager = () => {
  router.push('/content-manager')
}

const openAdmin = () => {
  router.push('/admin')
}

// Lifecycle
onMounted(() => {
  loadContent()
})

// Show debug on Ctrl+Shift+D
onMounted(() => {
  const handleKeydown = (e: KeyboardEvent) => {
    if (e.ctrlKey && e.shiftKey && e.key === 'D') {
      showDebug.value = !showDebug.value
    }
  }
  
  document.addEventListener('keydown', handleKeydown)
  
  // Cleanup
  return () => {
    document.removeEventListener('keydown', handleKeydown)
  }
})
</script>